/**
 * 浏览器存储实现（localStorage）
 */

import type { IStorage, StorageOptions, StorageItem } from './Storage'

export class BrowserStorage implements IStorage {
  private prefix: string
  private ttl?: number
  private fallback = new Map<string, string>()

  constructor(options: StorageOptions = {}) {
    this.prefix = options.prefix || 'csd_'
    this.ttl = options.ttl
  }

  private getKey(key: string): string {
    return `${this.prefix}${key}`
  }

  private getStorage(): Storage | null {
    try {
      if (typeof localStorage !== 'undefined') return localStorage
      if (typeof window !== 'undefined' && window.localStorage) return window.localStorage
      return null
    } catch {
      return null
    }
  }

  private isExpired(item: StorageItem): boolean {
    if (!item.ttl) return false
    return Date.now() - item.timestamp > item.ttl
  }

  getItem<T = any>(key: string): T | null {
    try {
      const fullKey = this.getKey(key)
      const storage = this.getStorage()
      const raw = storage?.getItem(fullKey) ?? this.fallback.get(fullKey) ?? null

      if (!raw) return null

      const item: StorageItem<T> = JSON.parse(raw)

      // 检查是否过期
      if (this.isExpired(item)) {
        this.removeItem(key)
        return null
      }

      return item.value
    } catch (error) {
      console.error(`[BrowserStorage] Failed to get item: ${key}`, error)
      return null
    }
  }

  setItem<T = any>(key: string, value: T): void {
    try {
      const fullKey = this.getKey(key)
      const item: StorageItem<T> = {
        value,
        timestamp: Date.now(),
        ttl: this.ttl
      }
      const raw = JSON.stringify(item)
      const storage = this.getStorage()

      if (storage) {
        storage.setItem(fullKey, raw)
      }
      this.fallback.set(fullKey, raw)
    } catch (error) {
      console.error(`[BrowserStorage] Failed to set item: ${key}`, error)
    }
  }

  removeItem(key: string): void {
    try {
      const fullKey = this.getKey(key)
      const storage = this.getStorage()
      storage?.removeItem(fullKey)
      this.fallback.delete(fullKey)
    } catch (error) {
      console.error(`[BrowserStorage] Failed to remove item: ${key}`, error)
    }
  }

  clear(): void {
    try {
      const keys = this.keys()
      keys.forEach((key) => this.removeItem(key))
    } catch (error) {
      console.error('[BrowserStorage] Failed to clear storage', error)
    }
  }

  hasItem(key: string): boolean {
    return this.getItem(key) !== null
  }

  keys(): string[] {
    try {
      const allKeys: string[] = []
      const storage = this.getStorage()
      if (storage) {
        for (let i = 0; i < storage.length; i++) {
          const key = storage.key(i)
          if (key && key.startsWith(this.prefix)) {
            allKeys.push(key.substring(this.prefix.length))
          }
        }
      }
      for (const key of this.fallback.keys()) {
        if (key.startsWith(this.prefix)) {
          allKeys.push(key.substring(this.prefix.length))
        }
      }
      return [...new Set(allKeys)]
    } catch (error) {
      console.error('[BrowserStorage] Failed to get keys', error)
      return []
    }
  }
}
